import * as SelectPrimitive from "@radix-ui/react-select";
import CheckIcon from "@redotech/redo-icons/check.svg";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronDownFilledIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import ChevronUpIcon from "@redotech/redo-icons/chevron-up.svg";
import * as React from "react";

import { cn } from "../../tailwind-utils";
import * as selectCss from "./select.module.css";

type SelectVariant = "default" | "redo";

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const defaultTriggerClassName =
  "data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring-accent shadow-sm aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive flex w-fit items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm whitespace-nowrap transition-[color,box-shadow] outline-none focus-visible:ring-[2px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4";

const redoTriggerClassName =
  "flex w-full items-center justify-between whitespace-nowrap outline-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0";

const redoTriggerStyles: React.CSSProperties = {
  backgroundColor: "var(--redo-colors-background-bg-primary)",
  border:
    "var(--redo-border-width) solid var(--redo-colors-border-border-primary)",
  padding: "0 8px",
  height: "28px",
  borderRadius: "var(--border-radius-sm)",
  gap: "4px",
  fontSize: "var(--redo-body-xsmall-text-size)",
};

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    size?: "sm" | "default";
    variant?: SelectVariant;
  }
>(
  (
    { className, size = "default", variant = "default", children, ...props },
    ref,
  ) => (
    <SelectPrimitive.Trigger
      className={cn(
        variant === "redo" ? redoTriggerClassName : defaultTriggerClassName,
        variant === "redo" && selectCss.redoTrigger,
        className,
      )}
      data-size={size}
      data-slot="select-trigger"
      data-variant={variant}
      ref={ref}
      style={variant === "redo" ? redoTriggerStyles : undefined}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        {variant === "redo" ? (
          <ChevronDownFilledIcon
            style={{ width: 16, height: 16, opacity: 0.5, flexShrink: 0 }}
          />
        ) : (
          <ChevronDownIcon className="size-4 opacity-50" />
        )}
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  ),
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) => (
  <SelectPrimitive.ScrollUpButton
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronUpIcon className="size-4" />
  </SelectPrimitive.ScrollUpButton>
);

const SelectScrollDownButton = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) => (
  <SelectPrimitive.ScrollDownButton
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    {...props}
  >
    <ChevronDownIcon className="size-4" />
  </SelectPrimitive.ScrollDownButton>
);

const contentClassName =
  "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 min-w-[8rem] overflow-x-hidden overflow-y-auto rounded-md border shadow-md";

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    variant?: SelectVariant;
  }
>(
  (
    {
      className,
      children,
      position: positionProp,
      variant = "default",
      ...props
    },
    ref,
  ) => {
    const position =
      positionProp ?? (variant === "redo" ? "popper" : "item-aligned");
    return (
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          avoidCollisions={variant === "redo" ? false : undefined}
          className={cn(
            contentClassName,
            position === "popper" &&
              "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
            className,
          )}
          position={position}
          ref={ref}
          side={variant === "redo" ? "bottom" : undefined}
          sideOffset={variant === "redo" ? 4 : undefined}
          style={{
            maxHeight: "var(--radix-select-content-available-height)",
            transformOrigin: "var(--radix-select-content-transform-origin)",
            ...(variant === "redo" ? { zIndex: 10_000 } : {}),
          }}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cn(
              "p-1",
              position === "popper" &&
                "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1",
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    );
  },
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) => (
  <SelectPrimitive.Label
    className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
    {...props}
  />
);

const defaultItemClassName =
  "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2";

const redoItemClassName =
  "relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2";

const redoItemStyles: React.CSSProperties = {
  fontSize: "var(--redo-body-xsmall-text-size)",
};

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    variant?: SelectVariant;
  }
>(({ className, children, variant = "default", ...props }, ref) => (
  <SelectPrimitive.Item
    className={cn(
      variant === "redo" ? redoItemClassName : defaultItemClassName,
      variant === "redo" && selectCss.redoItem,
      className,
    )}
    ref={ref}
    style={variant === "redo" ? redoItemStyles : undefined}
    {...props}
  >
    <span className="absolute right-2 flex size-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className="size-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = ({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) => (
  <SelectPrimitive.Separator
    className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
    {...props}
  />
);

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};
export type { SelectVariant };
